/**
 * What the strategic-analysis screen is actually showing, as one value.
 *
 * THE STATE THIS REPLACES WAS `run.data?.state ?? 'not_generated'`.
 *
 * That single expression collapsed four different situations into "no analysis
 * has been generated": the first render before the query resolves, a query that
 * failed, a report whose stored version this build cannot read, and a report
 * whose body fails its own contract. A founder who had generated an analysis was
 * told they had not, and offered a button to generate it again — which spends a
 * monthly run to produce a second copy of a report that already exists.
 *
 * Every state below is reachable and every one needs different words:
 *
 *   loading              nothing is known yet. Not an answer.
 *   read_error           the query failed. Retry is the action, not Generate.
 *   generation_error     the request to start a run was refused.
 *   missing_inputs       the analysis is blocked on something the founder holds.
 *   not_generated        genuinely never run.
 *   in_progress          queued / researching / synthesizing / validating.
 *   ready                a report that parsed against its own contract.
 *   insufficient_evidence a run that concluded honestly with no publishable body.
 *   failed               the run recorded why it failed.
 *   timed_out            the worker died without recording anything.
 *   unsupported_version  the report is newer than this build. Reload.
 *   malformed            the stored body fails its contract. Report it.
 *
 * `ready` is the ONLY state that carries a body, and the body has been through
 * `parseStoredReport`. Nothing downstream casts.
 */

import {
  type CurrentReportParse,
  type LegacyReport,
  parseReportForDisplay,
  type ReadyReport,
} from './report-contract';
import type { ResolvedRun, StrategicRunRow } from './status';
import type { AnalysisType } from './types';

export type ReportViewState =
  | { kind: 'loading' }
  | { kind: 'read_error'; error: unknown }
  | { kind: 'generation_error'; error: unknown }
  | { kind: 'missing_inputs' }
  | { kind: 'not_generated' }
  | { kind: 'in_progress'; runState: 'queued' | 'researching' | 'synthesizing' | 'validating' }
  | {
    kind: 'ready';
    /** The body, with its analysis type still attached. No cast anywhere. */
    report: ReadyReport;
    /**
     * THE RUN THE DISPLAYED REPORT BELONGS TO.
     *
     * Everything that captions the report — evidence, coverage, derivation, the
     * inputs-used snapshot, the bound assumption and thesis — is read from THIS
     * id. The hook used to select a run twice, once through `selectVisibleReport`
     * for the captions and once here for the body, and the two could land on
     * different runs: a refresh in flight showed the previous report's body with
     * the new run's coverage, which is a limitations band describing a search
     * that produced none of what is on screen.
     */
    displayedRunId: string;
    schemaVersion: string;
    /** The report on screen is the PREVIOUS one; a replacement is running. */
    showingPrevious: boolean;
    /** Ready, but built from inputs that have since changed. */
    isStale: boolean;
  }
  /**
   * A well-formed report from an older contract.
   *
   * NOT `ready`, and not an error either. The body parsed — it is exactly what
   * that version's writer produced — but it predates per-node claim keys,
   * identity provenance and grounded-statement labels, so the current components
   * would render it with no citations anywhere and no speculation labels on
   * statements that need them. Inventing those fields for an old row would be
   * manufacturing provenance, which is the one thing this feature must never do.
   */
  | {
    kind: 'legacy_report';
    schemaVersion: string;
    displayedRunId: string;
    /**
     * The old body, as ITSELF. Rendered by a reader that offers no citation
     * affordance, because a 1.0.0 row has no per-node claim keys to cite with —
     * see `LegacyReport`. Withholding it is what left a founder with published
     * analyses looking at an empty screen.
     */
    report: LegacyReport;
  }
  | { kind: 'insufficient_evidence' }
  | { kind: 'failed'; failureCode: string | null }
  | { kind: 'timed_out' }
  | { kind: 'unsupported_version'; schemaVersion: string }
  | { kind: 'malformed'; issues: string[] };

export interface ReportViewInput {
  analysisType: AnalysisType;
  /** True while the run query has produced nothing yet. */
  isLoading: boolean;
  /** The run query's failure. A failure is never an empty result. */
  readError?: unknown;
  /** The generate mutation's failure, if the founder just pressed the button. */
  generationError?: unknown;
  /** Blocked on a founder input. Decided upstream from readiness. */
  missingInputs?: boolean;
  /** The latest run, resolved for staleness and heartbeat death. */
  resolved: ResolvedRun | null;
  /** The row the resolved state came from, for its stored schema version. */
  latest: (StrategicRunRow & { schema_version?: string | null }) | null;
  /** The newest run that actually published, whatever has happened since. */
  lastReady: (StrategicRunRow & { schema_version?: string | null }) | null;
}

/**
 * Both candidate rows are parsed, and the LATEST ready one wins.
 *
 * Parsing the fallback matters as much as parsing the latest: a malformed latest
 * ready row used to fall through to "no report", which is the same screen a
 * founder who has never generated one sees. A corruption and an absence are
 * different problems with different actions, and only one of them is worth
 * spending a generation credit on.
 */
export function resolveReportView(input: ReportViewInput): ReportViewState {
  // ORDER MATTERS, and it is strictly most-certain-first. A read failure is
  // known with certainty; "not generated" is an inference from an absence, and
  // an inference must never outrank a fact.
  if (input.readError !== undefined && input.readError !== null) {
    return { kind: 'read_error', error: input.readError };
  }
  if (input.isLoading) return { kind: 'loading' };

  const resolved = input.resolved;
  if (!resolved || resolved.state === 'not_generated') {
    if (input.missingInputs) return { kind: 'missing_inputs' };
    // A generation error with nothing published is the whole story.
    if (input.generationError !== undefined && input.generationError !== null) {
      return { kind: 'generation_error', error: input.generationError };
    }
    return { kind: 'not_generated' };
  }

  switch (resolved.state) {
    case 'queued':
    case 'researching':
    case 'synthesizing':
    case 'validating': {
      // A run in flight still shows the PREVIOUS report when there is one —
      // blanking it makes a refresh feel like a deletion. Which previous reports
      // qualify is `displayOr`'s rule, and it is the same one for every state
      // below: only a report a reader can actually read.
      return displayOr(input, input.lastReady, true, {
        kind: 'in_progress',
        runState: resolved.state,
      });
    }

    case 'timed_out':
      return displayOr(input, input.lastReady, true, { kind: 'timed_out' });

    case 'failed':
      return displayOr(input, input.lastReady, true, {
        kind: 'failed',
        failureCode: resolved.failureCode,
      });

    case 'insufficient_evidence':
      return displayOr(input, input.lastReady, true, { kind: 'insufficient_evidence' });

    case 'ready': {
      const current = display(input, input.latest, false, resolved.isStale);
      if (current) return current;
      // The latest ready report cannot be shown. Fall back to an OLDER one that
      // can, rather than showing nothing — but name the problem, so a corruption
      // is never reported as an absence.
      const previous = display(input, input.lastReady, true, false);
      if (previous && previous.kind === 'ready' && input.lastReady?.id !== input.latest?.id) {
        return previous;
      }
      return problemFor(input.analysisType, input.latest);
    }
  }
}

/**
 * The one place a row becomes a display state.
 *
 * `showingPrevious` is passed in rather than inferred, because only the caller
 * knows whether this row is the latest or the fallback.
 */
function display(
  input: ReportViewInput,
  row: ReportViewInput['latest'],
  showingPrevious: boolean,
  isStale: boolean,
): ReportViewState | null {
  if (!row || row.report === null || row.report === undefined) return null;
  const parsed = parseReportForDisplay(input.analysisType, row.schema_version, row.report);
  if (parsed.status === 'valid') {
    return {
      kind: 'ready',
      report: parsed.report,
      displayedRunId: row.id,
      schemaVersion: row.schema_version ?? '',
      showingPrevious,
      isStale,
    };
  }
  if (parsed.status === 'legacy') {
    return {
      kind: 'legacy_report',
      schemaVersion: parsed.schemaVersion,
      displayedRunId: row.id,
      report: parsed.report,
    };
  }
  return null;
}

/**
 * A terminal or in-flight run still shows the last published report when one
 * exists — that report is a true record of what was assessed under the inputs it
 * names, and a failed refresh does not un-publish it.
 */
function displayOr(
  input: ReportViewInput,
  row: ReportViewInput['lastReady'],
  showingPrevious: boolean,
  otherwise: ReportViewState,
): ReportViewState {
  const shown = display(input, row, showingPrevious, false);
  // ONLY A REPORT A READER CAN ACTUALLY READ MAY STAND IN FOR WHAT THE RUN DID.
  //
  // This used to short-circuit on anything `display` produced, and a
  // `legacy_report` is not a shown report — it is the explanation for why there
  // is none. So a founder pressed "Refresh analysis" on a pre-contract report,
  // a run researched for 92 seconds, spent 62 provider calls and ended
  // `insufficient_evidence`, and the screen showed the identical legacy card it
  // had shown before. The button produced no observable change at all.
  //
  // The old rule also named a corruption ahead of the run's outcome, reasoning
  // that corruption is worth naming. It is — but not HERE. The reader has
  // already seen that notice; it is what prompted the refresh. The outcome is
  // the only new information on the screen. A stored report that cannot be read
  // still surfaces as a problem state through `problemFor` on the `ready` path,
  // which is where the report itself is what is being displayed.
  if (shown && shown.kind === 'ready') return shown;
  return otherwise;
}

function problemFor(
  analysisType: AnalysisType,
  row: ReportViewInput['latest'],
): ReportViewState {
  if (!row || row.report === null || row.report === undefined) {
    // `ready` with no report contradicts the table's own CHECK constraint.
    return { kind: 'malformed', issues: ['report: a ready run published no body'] };
  }
  const parsed: CurrentReportParse = parseReportForDisplay(
    analysisType,
    row.schema_version,
    row.report,
  );
  switch (parsed.status) {
    case 'unsupported_version':
      return { kind: 'unsupported_version', schemaVersion: parsed.schemaVersion };
    case 'malformed':
      return { kind: 'malformed', issues: parsed.issues };
    case 'legacy':
      return {
        kind: 'legacy_report',
        schemaVersion: parsed.schemaVersion,
        displayedRunId: row.id,
        report: parsed.report,
      };
    default:
      return { kind: 'not_generated' };
  }
}

/** Whether the state is one a reader can act on with Generate or Refresh. */
export function isGenerateOffered(state: ReportViewState): boolean {
  switch (state.kind) {
    // Nothing is known yet, or the read failed — Generate would be an action
    // taken on an unknown state, and the reader's action is retry.
    case 'loading':
    case 'read_error':
    case 'in_progress':
    // A corrupt or unreadable stored report is not a reason to spend a run:
    // regenerating may reproduce it, and the reader needs support, not a credit.
    case 'unsupported_version':
    case 'malformed':
      return false;
    default:
      // `legacy_report` DOES offer it: refreshing is exactly the fix, and it
      // produces a report under the current evidence contract.
      return true;
  }
}
